import {
  TextNode,
  SerializedTextNode,
  LexicalNode,
  NodeKey,
  $applyNodeReplacement,
  EditorConfig,
  DOMConversionMap,
  Spread,
} from 'lexical';

export type SerializedWhatsAppMonospaceNode = Spread<
  {
    type: 'whatsapp-monospace';
  },
  SerializedTextNode
>;

/**
 * WhatsApp Monospace Node
 * Uses single backtick (`text`) for inline code/monospace formatting
 */
export class WhatsAppMonospaceNode extends TextNode {
  static getType(): string {
    return 'whatsapp-monospace';
  }

  static clone(node: WhatsAppMonospaceNode): WhatsAppMonospaceNode {
    return new WhatsAppMonospaceNode(node.__text, node.__key);
  }



  // eslint-disable-next-line @typescript-eslint/no-useless-constructor
  constructor(text: string, key?: NodeKey) {
    super(text, key);
  }

  createDOM(config: EditorConfig): HTMLElement {
    const element = document.createElement('code');
    element.className = config.theme.whatsappMonospace || '';
    element.textContent = this.__text;
    return element;
  }

  updateDOM(prevNode: WhatsAppMonospaceNode, dom: HTMLElement): boolean {
    // If the text content has changed, update the DOM
    const prevText = prevNode.__text;
    const currentText = this.__text;
    
    if (prevText !== currentText) {
      dom.textContent = currentText;
    }
    
    // Return false because we handle the update ourselves
    return false;
  }

  static importDOM(): DOMConversionMap | null {
    return {
      code: () => ({
        conversion: () => ({ node: null }),
        priority: 1,
      }),
    };
  }

  static importJSON(serializedNode: SerializedWhatsAppMonospaceNode): WhatsAppMonospaceNode {
    const node = $createWhatsAppMonospaceNode(serializedNode.text);
    node.setFormat(serializedNode.format);
    node.setDetail(serializedNode.detail);
    node.setMode(serializedNode.mode);
    node.setStyle(serializedNode.style);
    return node;
  }

  exportJSON(): SerializedWhatsAppMonospaceNode {
    return {
      ...super.exportJSON(),
      type: 'whatsapp-monospace',
    };
  }

  exportMarkdown(): string {
    return `\`${this.__text}\``;
  }
}

export function $createWhatsAppMonospaceNode(text: string): WhatsAppMonospaceNode {
  return $applyNodeReplacement(new WhatsAppMonospaceNode(text));
}

export function $isWhatsAppMonospaceNode(
  node: LexicalNode | null | undefined
): node is WhatsAppMonospaceNode {
  return node instanceof WhatsAppMonospaceNode;
}
